import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

const storedExpansionSchema = z.enum(['expanded', 'collapsed']);

// Storage can be missing or throw (private windows, blocked site data), and its
// content is not ours to trust: anything unreadable falls back to the default.
const readExpansion = (storageKey: string): boolean | null => {
  try {
    const parsed = storedExpansionSchema.safeParse(window.localStorage.getItem(storageKey));
    return parsed.success ? parsed.data === 'expanded' : null;
  } catch {
    return null;
  }
};

const writeExpansion = (storageKey: string, isExpanded: boolean): void => {
  try {
    window.localStorage.setItem(storageKey, isExpanded ? 'expanded' : 'collapsed');
  } catch {
    // The section still works; it just opens on the default next time.
  }
};

interface ExpansionState {
  storageKey: string;
  isExpanded: boolean;
}

/**
 * Whether an expandable section is open, remembered in this browser under
 * `storageKey` so it comes back the way it was left after a reload. Opens by default
 * until it has been toggled. Follows the key: when it changes (the same page showing
 * another record) the state for the new key is read, so one record's choice does not
 * carry over to the next.
 */
export const usePersistedExpansion = (storageKey: string, defaultExpanded = true) => {
  const [state, setState] = useState<ExpansionState>(() => ({
    storageKey,
    isExpanded: readExpansion(storageKey) ?? defaultExpanded,
  }));

  // The state is for the key it was read under; a different key is read afresh.
  const isExpanded =
    state.storageKey === storageKey
      ? state.isExpanded
      : readExpansion(storageKey) ?? defaultExpanded;
  if (state.storageKey !== storageKey) setState({ storageKey, isExpanded });

  useEffect(() => {
    writeExpansion(storageKey, isExpanded);
  }, [storageKey, isExpanded]);

  const onToggle = useCallback(
    () =>
      setState((current) => ({
        storageKey,
        isExpanded: !(current.storageKey === storageKey
          ? current.isExpanded
          : readExpansion(storageKey) ?? defaultExpanded),
      })),
    [storageKey, defaultExpanded]
  );

  return { isExpanded, onToggle };
};
